<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="700px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template
      v-if="salesSlot"
      slot="dialogTitle"
    >
      <v-row no-gutters>
        <v-col>
          {{ salesSlot ? (salesSlot.yacht_manufacturer ? salesSlot.yacht_manufacturer.name + ' ' : '') + salesSlot.yacht_model : '' }}
        </v-col>
        <v-col cols="auto">
          <v-chip
            small
            label
            :color="statusData[salesSlot.status].color"
            text-color="white"
            class="ma-0 caption"
          >
            {{ statusData[salesSlot.status].label }}
          </v-chip>
        </v-col>
      </v-row>
      <div class="caption grey--text">
        Yacht Sales Slot |
        Created By: {{ salesSlot.created_by_user.staff.name }}, {{ salesSlot.created_at | moment('D MMM YYYY') }}
        <span v-if="salesSlot.last_updated_by_user">
          |
          {{ salesSlot.last_updated_by_user.staff.name }}, {{ salesSlot.updated_at | moment('D MMM YYYY') }}
        </span>
      </div>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text v-if="salesSlot">
      <v-card
        outlined
        class="mb-3"
      >
        <v-card-text>
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <span v-if="salesSlot.last_order_date">
                Last Order / Production Start: <span class="font-weight-medium">{{ salesSlot.last_order_date | moment('D MMM YYYY') }}</span>
                &middot;
              </span>
              Ex Factory: <span class="font-weight-medium">{{ salesSlot.ex_factory_date | moment('D MMM YYYY') }}</span>
            </v-col>
            <v-col
              v-if="!$can('view-only-partners-visible', 'Yacht')"
              cols="auto"
            >
              <v-icon
                x-small
                class="mr-2"
                :color="salesSlot.partners_visible ? 'success' : 'grey lighten-2'"
              >
                {{ salesSlot.partners_visible ? 'fa fa-check' : 'fa fa-times' }}
              </v-icon>
              <span>{{ salesSlot.partners_visible ? 'Visible to partner sales' : 'Invisible to partner sales' }}</span>
            </v-col>
          </v-row>
          <v-divider
            v-if="salesSlot.note"
            class="my-2"
          />
          {{ salesSlot.note }}
        </v-card-text>
      </v-card>

      <div v-if="!$can('view-only-partners-visible', 'Yacht') || $can('*', 'all')">
        <v-subheader class="mt-4">
          Yacht Sales For This Slot
        </v-subheader>

        <v-card
          v-if="salesSlot.yachts.length === 0"
          outlined
        >
          <v-card-text>
            No sales started yet.
          </v-card-text>
        </v-card>

        <v-list
          v-else
          class="pa-0"
        >
          <v-list-item
            v-for="yacht in salesSlot.yachts"
            :key="yacht.id"
            class="list--bordered"
            :to="{ name: 'yachtDashboard', params: { id: yacht.id }}"
            target="_blank"
          >
            <v-list-item-content>
              <v-list-item-title class="grey--text text--darken-3">
                <v-row no-gutters>
                  <v-col>
                    #{{ yacht.id }} &middot; {{ yacht.name || 'No Name' }}
                  </v-col>
                  <v-col
                    v-if="yacht.country"
                    cols="auto"
                  >
                    <v-chip
                      label
                      small
                      outlined
                      style="max-width: 110px"
                      class="text-truncate"
                    >
                      <flag
                        v-if="yacht.country"
                        :iso="yacht.country.code"
                        class="mr-1"
                        :squared="false"
                      />
                      <span
                        v-if="yacht.destination_base"
                      >
                        {{ yacht.destination_base.name }}
                      </span>
                    </v-chip>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="pl-2 pr-3"
                  >
                    <v-chip
                      small
                      label
                      :color="investmentStatusData[yacht.investment_status].color"
                      text-color="white"
                      class="ma-0 caption"
                    >
                      {{ investmentStatusData[yacht.investment_status].label }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-list-item-title>
              <v-list-item-subtitle class="caption grey--text">
                Sales Rep: {{ yacht.sales_rep ? yacht.sales_rep.name : 'N/A' }}
                &middot;
                Yacht owner: {{ yacht.owner ? yacht.owner.name : 'N/A' }}
                &middot;
                {{ yacht.updated_at | moment('from') }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon
                small
                class="v-icon--moves"
                color="grey"
              >
                arrow_forward
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="salesSlot && salesSlot.status !== statusClosed && $can('edit', 'YachtSalesSlot')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        Close Slot
      </v-btn>
      <v-btn
        v-if="$can('edit', 'YachtSalesSlot')"
        small
        outlined
        color="grey"
        @click="yachtSalesSlotEditVisible = true"
      >
        Edit
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        @click="yachtEditVisible = true"
      >
        Start New Sales
        <v-icon
          right
          small
        >
          add
        </v-icon>
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Close yacht sales slot?"
      subtext="In case this slot is not available anymore at the yard, close it."
      cta-text="Yes, close"
      @confirmed="closeSlot()"
    />

    <yacht-sales-slot-edit-dialog
      v-if="salesSlot"
      :sales-slot="salesSlot"
      :visible.sync="yachtSalesSlotEditVisible"
      @update:sales-slot="salesSlot = $event; $emit('updated:sales-slot', $event)"
      @deleted:sales-slot="modalVisible = false; $emit('deleted:sales-slot')"
    />

    <yacht-edit-dialog
      v-if="salesSlot"
      :visible.sync="yachtEditVisible"
      :preselect-yacht-manufacturer-id="salesSlot.yacht_manufacturer ? salesSlot.yacht_manufacturer.id : undefined"
      :sales-slot="salesSlot"
      @update:yacht="yachtCreated"
    />
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ContentDialog from '@/components/helpers/Dialog'
// import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import YachtSalesSlot from '@/models/YachtSalesSlot'
import Yacht from '@/models/Yacht'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import YachtEditDialog from '@/components/business/yachts/YachtEditDialog'
import YachtSalesSlotEditDialog from '@/components/business/yachts/YachtSalesSlotEditDialog'

export default {
  name: 'YachtSalesSlotViewDialog',
  components: {
    YachtSalesSlotEditDialog,
    YachtEditDialog,
    ConfirmDeleteDialog,
    ContentDialog
    // ConfirmDeleteDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: Boolean,
    salesSlotId: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      fetching: false,
      salesSlot: undefined,
      yachtEditVisible: false,
      yachtSalesSlotEditVisible: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    salesSlotId: function (value) {
      if (!value) {
        this.salesSlot = undefined
        return
      }

      this.fetchSlot()
    }
  },
  created() {
    this.fetchSlot()

    this.investmentStatusData = Yacht.investmentStatusData
    this.statusData = YachtSalesSlot.statusData
    this.statusClosed = YachtSalesSlot.STATUS_CLOSED
  },
  methods: {
    async fetchSlot() {
      if (!this.salesSlotId) return

      this.fetching = true

      try {
        this.salesSlot = await YachtSalesSlot.api.get(this.salesSlotId)
      } catch (e) {
        this.$store.dispatch('alert', {
          color: 'error',
          text: 'Error getting sales slot'
        })
      } finally {
        this.fetching = false
      }
    },
    async closeSlot() {
      this.deleting = true

      try {
        this.salesSlot = await YachtSalesSlot.api.update({ status: YachtSalesSlot.STATUS_CLOSED }, this.salesSlot.id)

        this.deleteConfirmationDialogVisible = false
        // this.dialogVisible = false
        this.$emit('updated:sales-slot', this.salesSlot)

        this.$store.dispatch('alert', {
          color: 'success',
          text: 'Slot Closed'
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          color: 'error',
          text: 'Something went wrong'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    yachtCreated(yacht) {
      this.$router.push({ name: 'yachtDashboard', params: { id: yacht.id } })
    }
    // async deleteSlot() {
    //   this.deleting = true
    //
    //   try {
    //     await YachtSalesSlot.api.destroy(this.slotDirty.id)
    //
    //     this.$store.dispatch('alert', {
    //       'text': 'Sales stock slot deleted',
    //       'color': 'info'
    //     })
    //     this.modalVisible = false
    //   } catch (e) {
    //     if (e.response && e.response.status === 403) {
    //       this.$store.dispatch('alert', {
    //         'text': 'Insufficient privileges.',
    //         'color': 'error'
    //       })
    //     } else {
    //       this.$store.dispatch('alert', {
    //         'text': 'Something went wrong.',
    //         'color': 'error'
    //       })
    //     }
    //
    //     throw e
    //   } finally {
    //     this.deleteConfirmationDialogVisible = false
    //     this.deleting = false
    //   }
    // },
  }
}
</script>
